import { useEffect, useState } from 'react';
import { mediaAttachmentService } from '../services/mediaAttachmentService';
import { MediaAttachmentDto } from '../types/mediaAttachment';

/**
 * Bildekilde for MediaAttachment uavhengig av lagringsstrategi (TW 36393708).
 *
 * Små bilder (≤ 100 KB) ligger inline som dataUrl i listesvaret; større ligger på disk
 * og har dataUrl = null. For de siste hentes innholdet autentisert fra
 * GET media/{id}/content og eksponeres som object-URL. Object-URL-er frigis når
 * vedleggslista endres eller komponenten avmonteres.
 *
 * Returnerer et oppslag id → src (dataUrl eller blob:-URL). Mangler oppføring er bildet
 * enten fortsatt under henting eller utilgjengelig (fil mangler på disk).
 */
export function useMediaContentUrls(attachments: MediaAttachmentDto[]): Record<number, string> {
  const [urls, setUrls] = useState<Record<number, string>>({});

  // Stabil nøkkel så effekten ikke kjører på ny array-identitet med samme innhold.
  const key = attachments.map(a => `${a.id}:${a.dataUrl ? 1 : 0}`).join(',');

  useEffect(() => {
    let cancelled = false;
    const created: string[] = [];
    const next: Record<number, string> = {};

    const inline = attachments.filter(a => a.dataUrl);
    const remote = attachments.filter(a => !a.dataUrl);
    inline.forEach(a => { next[a.id] = a.dataUrl!; });

    if (remote.length === 0) {
      setUrls(next);
      return;
    }

    // Vis inline-bildene med én gang; diskbildene fylles inn etter hvert.
    setUrls({ ...next });

    Promise.all(remote.map(async a => {
      try {
        const url = await mediaAttachmentService.fetchContentObjectUrl(a.id);
        if (cancelled) { URL.revokeObjectURL(url); return; }
        created.push(url);
        next[a.id] = url;
        setUrls({ ...next });
      } catch (err) {
        console.warn(`Kunne ikke hente medieinnhold for vedlegg ${a.id}:`, err);
      }
    }));

    return () => {
      cancelled = true;
      created.forEach(u => URL.revokeObjectURL(u));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  return urls;
}
